import { useEffect, useState } from 'react';
import { usePublicClient, useAccount } from 'wagmi';
import { formatUnits } from 'viem';
import { ERC20_ABI } from '../config/contracts';

// High-value tokens that should always show decimals
const HIGH_VALUE_TOKENS = ['BNB', 'WBNB', 'ETH', 'BTCB'];

export const useBalance = (address, token) => {
  const [balance, setBalance] = useState(0);
  const [formatted, setFormatted] = useState('0');
  const [value, setValue] = useState(0n);
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  
  const publicClient = usePublicClient();
  const { isConnected } = useAccount();

  useEffect(() => {
    let isMounted = true;
    let abortController = new AbortController();

    const fetchBalance = async () => {
      if (!address || !token || !publicClient || !isConnected) {
        if (isMounted) {
          setBalance(0);
          setFormatted('0');
          setValue(0n);
        }
        return;
      }

      if (isMounted) {
        setIsLoading(true);
        setIsError(false);
      }

      try {
        let balanceValue;
        const tokenDecimals = token.decimals || 18; // CRITICAL: Use actual token decimals

        if (token.isNative) {
          // Get native BNB balance
          balanceValue = await publicClient.getBalance({ address });
          console.log(`[Balance] ${token.symbol} (native):`, formatUnits(balanceValue, tokenDecimals));
        } else {
          // Get ERC20 token balance
          balanceValue = await publicClient.readContract({
            address: token.address,
            abi: ERC20_ABI,
            functionName: 'balanceOf',
            args: [address],
          });
          console.log(`[Balance] ${token.symbol} (${token.address}):`, formatUnits(balanceValue, tokenDecimals));
        }

        // CRITICAL FIX: Use formatUnits with actual token decimals instead of formatEther
        // formatEther always uses 18 decimals which is WRONG for tokens with different decimals
        const balanceString = formatUnits(balanceValue, tokenDecimals);
        const balanceNumber = Number(balanceString);
        
        // For very large numbers (sextillion+), use BigInt string conversion
        // to avoid Number overflow (max safe integer is ~9e15)
        const isVeryLargeNumber = balanceString.length > 15 || parseFloat(balanceString) > 1e15;
        
        let actualBalance;
        if (isVeryLargeNumber) {
          // Use parseFloat for display, but keep precision in string
          actualBalance = parseFloat(balanceString);
        } else {
          actualBalance = balanceNumber;
        }
        
        // Conditional formatting based on token type
        let balanceFormatted;
        const isHighValueToken = HIGH_VALUE_TOKENS.includes(token.symbol?.toUpperCase());
        
        if (isHighValueToken) {
          // High-value tokens (BNB, ETH, etc.) always show decimals
          balanceFormatted = actualBalance.toFixed(6).replace(/\.?0+$/, '');
        } else {
          // Low-value tokens - preserve large numbers
          if (actualBalance > 1e12) {
            // Trillion+ tokens: show with grouping
            balanceFormatted = actualBalance.toLocaleString('en-US', { 
              maximumFractionDigits: 2,
              useGrouping: true 
            });
          } else if (actualBalance > 1000) {
            balanceFormatted = Math.floor(actualBalance).toLocaleString();
          } else if (actualBalance > 1) {
            balanceFormatted = actualBalance.toFixed(2);
          } else {
            balanceFormatted = actualBalance.toFixed(6);
          }
        }

        if (isMounted && !abortController.signal.aborted) {
          setBalance(actualBalance);
          setFormatted(balanceFormatted);
          setValue(balanceValue);
        }
      } catch (error) {
        if (!abortController.signal.aborted) {
          console.error(`[Balance] Error fetching ${token?.symbol}:`, error);
          if (isMounted) {
            setIsError(true);
            setBalance(0);
            setFormatted('0');
            setValue(0n);
          }
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    fetchBalance();

    // Refetch every 15 seconds (increased from 10s to reduce load)
    const interval = setInterval(fetchBalance, 15000);
    
    return () => {
      isMounted = false;
      abortController.abort();
      clearInterval(interval);
    };
  }, [address, token?.address, token?.isNative, token?.symbol, token?.decimals, publicClient, isConnected]);

  return {
    balance,
    value,
    formatted,
    symbol: token?.symbol || '',
    decimals: token?.decimals || 18,
    isLoading,
    isError,
    refetch: () => {} // Placeholder
  };
};
